// client/src/components/plataforma/contas/PublicPageSettingsPanel.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAccountById } from '../../../hooks/useAccounts';
import { useMutateAccountPage } from '../../../hooks/useAccountPage';
import { Loader2, Eye, Globe, ToggleLeft, ToggleRight, XCircle } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import { FormStepper, FormStepNavigation } from '../../shared/FormStepper';

const STEP_LABELS = ['Configurações da Página'];

// ✅ NUNCA usar window.location.origin aqui — o client roda na porta 3000
// (admin), que não serve /conta/*; quem serve o site público da conta é o
// servidor Express (porta 50000 em dev, domínio 2 configurado em
// PUBLIC_CONSUMER_DOMAIN em produção).
const SITE_CONTA_URL = process.env.REACT_APP_SITE_CONTA_URL || 'http://localhost:50000';
// Conta vinculada a uma franquia é servida em {slugFranquia}.<domínio>/{slug}
// (sem prefixo /conta/) — ver loaders/routes.js no server. Sem essa
// franquia é servida em <domínio>/conta/{slug}. Usar o slug errado (ou
// ignorar a franquia) manda o usuário pra uma URL que não resolve pra essa
// conta (bug confirmado ao vivo em 2026-09-07).
const buildContaPublicUrl = (account) => {
    const slug = account?.slug;
    if (!slug) return null;
    const franquiaSlug = account?.franquia?.slug;
    if (franquiaSlug) {
        const base = SITE_CONTA_URL.replace(/^https?:\/\//, '');
        const protocol = SITE_CONTA_URL.startsWith('http://') ? 'http://' : 'https://';
        return `${protocol}${franquiaSlug}.${base}/${slug}`;
    }
    return `${SITE_CONTA_URL}/conta/${slug}`;
};

const PublicPageSettingsPanel = ({ accountId, onBack }) => {
    const { data: account, isLoading } = useAccountById(accountId);
    const { updatePageConfig, togglePublicPage } = useMutateAccountPage();

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        metaTitle: '',
        metaDescription: '',
        primaryColor: '#2563eb',
        secondaryColor: '#10b981',
        showServices: true,
        showTestimonials: true,
        showContact: true,
        showSocialLinks: true,
        showLoja: true,
        lojaCategorias: ['servicos', 'produtos', 'recursos', 'pacotes', 'ofertas', 'cursos'],
        showUnidades: true,
        showSegmentos: true,
        customCss: '',
        customJs: ''
    });

    useEffect(() => {
        if (account?.pageConfig) {
            setFormData(prev => ({ ...prev, ...account.pageConfig }));
        }
    }, [account]);

    const handleInputChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }, []);

    const handleColorChange = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleToggleSection = useCallback((field) => {
        setFormData(prev => ({ ...prev, [field]: !prev[field] }));
    }, []);

    const handleToggleLojaCategoria = useCallback((categoria) => {
        setFormData(prev => {
            const atuais = prev.lojaCategorias || [];
            const proximo = atuais.includes(categoria)
                ? atuais.filter(c => c !== categoria)
                : [...atuais, categoria];
            return { ...prev, lojaCategorias: proximo };
        });
    }, []);

    const handleCancel = useCallback(() => {
        if (account?.pageConfig) {
            setFormData(prev => ({ ...prev, ...account.pageConfig }));
        }
        if (onBack) onBack();
    }, [account, onBack]);

    const handleTogglePage = useCallback(() => {
        if (!accountId) return;
        togglePublicPage.mutate(accountId);
    }, [accountId, togglePublicPage]);

    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        if (!accountId) return;
        updatePageConfig.mutate({ accountId, ...formData });
    }, [accountId, formData, updatePageConfig]);

    const isEnabled = account?.pageConfig?.enabled;
    const isPending = updatePageConfig.isPending || togglePublicPage.isPending;

    return (
        <div className="page-management-container">
            <PageHeader
                icon={<Globe />}
                title="Página Pública"
                subtitle={isLoading ? null : (isEnabled ? 'Visível ao público' : 'Não publicada')}
                actions={
                    <>
                        <button
                            type="button"
                            onClick={handleTogglePage}
                            disabled={isPending || isLoading}
                            className="action-button"
                            style={{
                                background: isEnabled ? '#10b981' : '#ef4444',
                                borderColor: isEnabled ? '#10b981' : '#ef4444',
                                color: '#fff'
                            }}
                        >
                            {isEnabled
                                ? <><ToggleRight size={18} className="icon" /> Ativo (clique p/ desativar)</>
                                : <><ToggleLeft size={18} className="icon" /> Desativado (clique p/ ativar)</>
                            }
                        </button>
                        {isEnabled && account?.slug && (
                            <button
                                type="button"
                                onClick={() => window.open(buildContaPublicUrl(account), '_blank')}
                                className="action-button action-button-secondary"
                            >
                                <Eye size={18} className="icon" /> Visualizar
                            </button>
                        )}
                    </>
                }
            />

            {isLoading ? (
                <div className="table-loading-state" style={{ minHeight: 200 }}>
                    <Loader2 size={24} className="animate-spin" />
                    <span>Carregando configurações...</span>
                </div>
            ) : (
                <form onSubmit={handleSubmit}>
                    <div className="content-panel">
                        <FormStepper currentStep={1} steps={STEP_LABELS} />

                        {/* Link da página pública */}
                        {isEnabled && account?.slug && (
                            <div className="form-section">
                                <div style={{ background: 'var(--color-primary-light, #eff6ff)', border: '1px solid var(--color-primary-border, #bfdbfe)', borderRadius: 8, padding: '0.875rem 1rem' }}>
                                    <p style={{ fontWeight: 600, color: 'var(--color-primary)', marginBottom: 2 }}>
                                        Sua página pública
                                    </p>
                                    <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                                        {buildContaPublicUrl(account)}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Informações Básicas */}
                        <div className="form-section">
                            <h4 className="form-section-title">Informações Básicas</h4>
                            <div className="form-grid-container-cols-2">
                                <div className="form-group">
                                    <label className="form-label">Título da Página</label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        className="form-input"
                                        placeholder="Ex: Minha Clínica"
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Meta Título (SEO)</label>
                                    <input
                                        type="text"
                                        name="metaTitle"
                                        value={formData.metaTitle}
                                        onChange={handleInputChange}
                                        className="form-input"
                                        placeholder="Ex: Minha Clínica – Depilação a laser"
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Descrição</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    rows={3}
                                    className="form-input"
                                    placeholder="Descreva brevemente sua empresa e serviços..."
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Meta Descrição (SEO)</label>
                                <textarea
                                    name="metaDescription"
                                    value={formData.metaDescription}
                                    onChange={handleInputChange}
                                    rows={2}
                                    className="form-input"
                                    placeholder="Descrição para motores de busca..."
                                />
                            </div>
                        </div>

                        {/* Cores */}
                        <div className="form-section">
                            <h4 className="form-section-title">Cores da Página</h4>
                            <div className="form-grid-container-cols-2">
                                <div className="form-group">
                                    <label className="form-label">Cor Primária</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <input
                                            type="color"
                                            value={formData.primaryColor}
                                            onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                                            style={{ width: 40, height: 40, border: '1px solid var(--color-border)', borderRadius: 6, cursor: 'pointer', padding: 2 }}
                                        />
                                        <input
                                            type="text"
                                            value={formData.primaryColor}
                                            onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                                            className="form-input"
                                        />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Cor Secundária</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <input
                                            type="color"
                                            value={formData.secondaryColor}
                                            onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                                            style={{ width: 40, height: 40, border: '1px solid var(--color-border)', borderRadius: 6, cursor: 'pointer', padding: 2 }}
                                        />
                                        <input
                                            type="text"
                                            value={formData.secondaryColor}
                                            onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                                            className="form-input"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Seções visíveis */}
                        <div className="form-section">
                            <h4 className="form-section-title">Seções da Página</h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                {[
                                    { key: 'showServices',     label: 'Mostrar Seção de Serviços' },
                                    { key: 'showTestimonials', label: 'Mostrar Seção de Depoimentos' },
                                    { key: 'showContact',      label: 'Mostrar Seção de Contato' },
                                    { key: 'showSocialLinks',  label: 'Mostrar Links de Redes Sociais' },
                                    { key: 'showUnidades',     label: 'Mostrar Nossas Unidades' },
                                    { key: 'showSegmentos',    label: 'Mostrar Segmentos de Atuação' },
                                    { key: 'showLoja',         label: 'Mostrar Nossa Loja (Explore tudo o que temos a oferecer)' },
                                ].map(({ key, label }) => (
                                    <div
                                        key={key}
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', background: 'var(--color-bg-alt, #f9fafb)', borderRadius: 8, border: '1px solid var(--color-border)' }}
                                    >
                                        <span style={{ fontSize: '0.875rem', color: 'var(--color-text)' }}>{label}</span>
                                        <button
                                            type="button"
                                            onClick={() => handleToggleSection(key)}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: formData[key] ? '#10b981' : '#ef4444', display: 'flex', alignItems: 'center' }}
                                        >
                                            {formData[key]
                                                ? <ToggleRight size={28} />
                                                : <ToggleLeft size={28} />
                                            }
                                        </button>
                                    </div>
                                ))}
                            </div>

                            {formData.showLoja && (
                                <div style={{ marginTop: '0.75rem', padding: '0.75rem 1rem', background: 'var(--color-bg-alt, #f9fafb)', borderRadius: 8, border: '1px solid var(--color-border)' }}>
                                    <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text)' }}>O que exibir na loja</span>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '0.5rem' }}>
                                        {[
                                            { value: 'servicos', label: 'Nossos Serviços' },
                                            { value: 'recursos',  label: 'Locações' },
                                            { value: 'pacotes',  label: 'Nossos Pacotes' },
                                            { value: 'produtos', label: 'Nossos Produtos' },
                                            { value: 'ofertas',  label: 'Ofertas' },
                                            { value: 'cursos',   label: 'Nossos Cursos' },
                                        ].map(({ value, label }) => (
                                            <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.8125rem', cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={(formData.lojaCategorias || []).includes(value)}
                                                    onChange={() => handleToggleLojaCategoria(value)}
                                                />
                                                {label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Códigos Personalizados */}
                        <div className="form-section">
                            <h4 className="form-section-title">Códigos Personalizados</h4>
                            <div className="form-group">
                                <label className="form-label">CSS Personalizado</label>
                                <textarea
                                    name="customCss"
                                    value={formData.customCss}
                                    onChange={handleInputChange}
                                    rows={4}
                                    className="form-input"
                                    style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}
                                    placeholder="/* Seu CSS personalizado aqui */"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">JavaScript Personalizado</label>
                                <textarea
                                    name="customJs"
                                    value={formData.customJs}
                                    onChange={handleInputChange}
                                    rows={4}
                                    className="form-input"
                                    style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}
                                    placeholder="// Seu JavaScript personalizado aqui"
                                />
                            </div>
                        </div>

                        <div className="form-actions">
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="action-button action-button-secondary"
                                disabled={isPending}
                            >
                                <XCircle size={18} className="icon" /> Cancelar
                            </button>
                            <FormStepNavigation
                                currentStep={1}
                                totalSteps={1}
                                onBack={() => {}}
                                onNext={() => {}}
                                isSubmitting={isPending}
                                submitLabel="Salvar Configurações"
                            />
                        </div>
                    </div>
                </form>
            )}
        </div>
    );
};

export default PublicPageSettingsPanel;
